import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon, 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  GlobeAltIcon,
  DocumentTextIcon,
  BanknotesIcon,
  IdentificationIcon,
  CheckCircleIcon,
  PhotoIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import toast from 'react-hot-toast';

const CompanySettings = () => {
  const { companySettings, updateCompanySettings, isLoading } = useCompanySettings();
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (companySettings) {
      setFormData(companySettings);
    }
  }, [companySettings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const success = await updateCompanySettings(formData);
    if (success) {
      setHasChanges(false);
    }
    setIsSaving(false);
  };

  const handleReset = () => {
    setFormData(companySettings);
    setHasChanges(false);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image');
      event.target.value = ''; // Reset input
      return;
    }

    // Validate file size (max 1MB for better performance)
    if (file.size > 1 * 1024 * 1024) {
      toast.error('La taille du fichier ne doit pas dépasser 1MB');
      event.target.value = ''; // Reset input
      return;
    }

    // Show loading toast
    const loadingToast = toast.loading('Upload du logo en cours...');

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const base64 = e.target.result;
        
        // Additional size check for base64 (should be less than 1.5MB when encoded)
        if (base64.length > 1.5 * 1024 * 1024) {
          toast.dismiss(loadingToast);
          toast.error('Le fichier encodé est trop volumineux. Veuillez choisir une image plus petite.');
          event.target.value = '';
          return;
        }
        
        handleChange('logo', base64);
        toast.dismiss(loadingToast);
        toast.success('Logo uploadé avec succès');
      } catch (error) {
        toast.dismiss(loadingToast);
        toast.error('Erreur lors du traitement du fichier');
        event.target.value = '';
      }
    };
    reader.onerror = () => {
      toast.dismiss(loadingToast);
      toast.error('Erreur lors du chargement du fichier');
      event.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  const handleLogoRemove = () => {
    handleChange('logo', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Logo supprimé');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white mb-2">Informations de l'entreprise</h2>
          <p className="text-white/60">Gérez les informations qui apparaîtront sur vos documents</p>
        </div>
        {hasChanges && (
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Logo Upload */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 mb-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <PhotoIcon className="h-6 w-6 text-blue-400" />
          <h3 className="text-lg font-medium text-white">Logo de l'entreprise</h3>
        </div>

        <div className="space-y-4">
          <p className="text-white/60 text-sm">
            Uploadez le logo de votre entreprise qui apparaîtra sur tous vos documents.
          </p>
          
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              <div className="w-32 h-32 bg-white/5 border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center overflow-hidden">
                {formData.logo ? (
                  <img 
                    src={formData.logo} 
                    alt="Logo preview" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <PhotoIcon className="h-8 w-8 text-white/40 mx-auto mb-2" />
                    <p className="text-white/40 text-xs">Aucun logo</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Controls */}
            <div className="flex-1">
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                
                <div className="flex gap-3">
                  <label
                    htmlFor="logo-upload"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition-all flex items-center gap-2"
                  >
                    <PhotoIcon className="h-4 w-4" />
                    {formData.logo ? 'Changer le logo' : 'Uploader le logo'}
                  </label>
                  
                  {formData.logo && (
                    <button
                      onClick={handleLogoRemove}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all flex items-center gap-2"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Supprimer
                    </button>
                  )}
                </div>
                
                <p className="text-white/50 text-xs">
                  Formats acceptés: JPG, PNG, GIF. Taille maximale: 1MB.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Company Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center gap-3 mb-6">
          <BuildingOfficeIcon className="h-6 w-6 text-blue-400" />
          <h3 className="text-lg font-medium text-white">Informations générales</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Nom de l'entreprise
            </label>
            <input
              type="text"
              value={formData.companyName || ''}
              onChange={(e) => handleChange('companyName', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="STOCKPRO INVENTORY"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Nom en arabe (optionnel)
            </label>
            <input
              type="text"
              value={formData.companyNameArabic || ''}
              onChange={(e) => handleChange('companyNameArabic', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ستوك برو انفانتري"
              dir="rtl"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Description
            </label>
            <input
              type="text"
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Système de Gestion d'Inventaire"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Sous-titre
            </label>
            <input
              type="text"
              value={formData.descriptionSubtitle || ''}
              onChange={(e) => handleChange('descriptionSubtitle', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Solutions logicielles d'entreprise"
            />
          </div>
        </div>
      </motion.div>

      {/* Contact Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center gap-3 mb-6">
          <PhoneIcon className="h-6 w-6 text-green-400" />
          <h3 className="text-lg font-medium text-white">Coordonnées</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Adresse
            </label>
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Alger Centre, Algérie"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Code postal
            </label>
            <input
              type="text"
              value={formData.postalCode || ''}
              onChange={(e) => handleChange('postalCode', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="16000"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Téléphone
            </label>
            <input
              type="text"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="+213 (0) 21 XX XX XX"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="contact@stockpro.dz"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-white/80 text-sm font-medium mb-2">
              Site web (optionnel)
            </label>
            <input
              type="url"
              value={formData.website || ''}
              onChange={(e) => handleChange('website', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="www.stockpro.dz"
            />
          </div>
        </div>
      </motion.div>

      {/* Legal Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center gap-3 mb-6">
          <IdentificationIcon className="h-6 w-6 text-yellow-400" />
          <h3 className="text-lg font-medium text-white">Informations légales</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Article N°
            </label>
            <input
              type="text"
              value={formData.articleNumber || ''}
              onChange={(e) => handleChange('articleNumber', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="00/00-000123456-B12"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              NIF N°
            </label>
            <input
              type="text"
              value={formData.nifNumber || ''}
              onChange={(e) => handleChange('nifNumber', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="000123456789"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              NIS N°
            </label>
            <input
              type="text"
              value={formData.nisNumber || ''}
              onChange={(e) => handleChange('nisNumber', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="000123456789"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              RC N°
            </label>
            <input
              type="text"
              value={formData.rcNumber || ''}
              onChange={(e) => handleChange('rcNumber', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="98/00-0123456-B21"
            />
          </div>
        </div>
      </motion.div>

      {/* Banking Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center gap-3 mb-6">
          <BanknotesIcon className="h-6 w-6 text-purple-400" />
          <h3 className="text-lg font-medium text-white">Informations bancaires</h3>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Compte bancaire principal
            </label>
            <input
              type="text"
              value={formData.bankAccount1 || ''}
              onChange={(e) => handleChange('bankAccount1', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="1234567890123456 CPA ALGER"
            />
          </div>

          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Compte bancaire secondaire (optionnel)
            </label>
            <input
              type="text"
              value={formData.bankAccount2 || ''}
              onChange={(e) => handleChange('bankAccount2', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="9876543210987654 BADR ALGER"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Taux de TVA par défaut (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.defaultTaxRate || ''}
                onChange={(e) => handleChange('defaultTaxRate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="19"
              />
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Devise
              </label>
              <select
                value={formData.currency || 'DA'}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="DA">Dinar Algérien (DA)</option>
                <option value="EUR">Euro (€)</option>
                <option value="USD">Dollar US ($)</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CompanySettings;